<template>
<main class="flex-grow-1 px-3 py-3">
  <!-- loading / error states -->
  <div v-if="loading" class="loading-indicator text-center py-5">
    Loading overview...
  </div>
  <div v-else-if="error" class="error-message text-center py-5 text-danger">
    An error occurred: {{ error }}
  </div>

  <div v-else>
    <!-- ===================== -->
    <!-- MAIN GRID -->
    <!-- ===================== -->
    <div class="row g-3">

      <!-- LEFT COLUMN - Latest Assessment -->
      <div class="col-lg-7">

        <!-- Latest Assessment -->
        <div class="glassy-table border rounded-lg shadow-sm p-4 h-100">

          <div class="d-flex justify-content-between mb-3">
            <h6 class="fw-bold">Latest Assessment</h6>
            <small class="text-muted">{{ formatDate(overview?.latest_assessment_date) }}</small>
          </div>

          <!-- Assessment Sections -->
          <div v-if="!overview?.sections || overview.sections.length === 0" class="text-muted text-center py-4">
            <p>No assessment sections have been filled out yet.</p>
          </div>

          <div v-else>
            <div v-for="(section, idx) in overview.sections" :key="idx" class="mb-4 pb-3" :class="{ 'border-bottom': idx < overview.sections.length - 1 }">

              <div class="d-flex justify-content-between small mb-1">
                <span>{{ section.section_name }}</span>
                <span class="fw-bold" :class="getMetricClass(calculateSectionPercentage(section))">{{ calculateSectionPercentage(section) }}%</span>
              </div>

              <div class="progress" style="height:6px">
                <div class="progress-bar" :class="getMetricBarClass(calculateSectionPercentage(section))" :style="{ width: calculateSectionPercentage(section) + '%' }"></div>
              </div>

            </div>

            <!-- Overall Score -->
            <div class="bg-light rounded p-3 d-flex justify-content-between align-items-center mt-4">
              <span class="fw-bold">Overall Score</span>
              <h5 class="fw-bold text-success mb-0">{{ overview?.overall_score || 0 }}%</h5>
            </div>
          </div>

        </div>

      </div>

      <!-- RIGHT COLUMN - Status & Info -->
      <div class="col-lg-5">

        <!-- Current Status -->
        <div class="glassy-table border rounded-lg shadow-sm p-4 mb-3">

          <h6 class="fw-bold mb-3">Current Status</h6>

          <span class="badge mb-3" :class="overview?.overall_score >= 75 ? 'bg-success' : 'bg-warning'">
            {{ overview?.status || 'N/A' }}
          </span>

      <div class="d-flex justify-content-between border-top pt-2 small">
        <span class="text-muted">Inspector</span>
            <span class="fw-semibold">{{ overview?.inspector ? overview.inspector : 'Not Assigned' }}</span>
      </div>

          <div class="d-flex justify-content-between border-top pt-2 small">
            <span class="text-muted">Visit Date</span>
            <span class="fw-semibold">{{ formatDate(overview?.visit_date) }}</span>
          </div>

          <div class="d-flex justify-content-between border-top pt-2 small">
            <span class="text-muted">Times Monitored</span>
            <span class="fw-semibold">{{ overview?.times_monitored || 0 }} cycle(s)</span>
          </div>

        </div>

        <!-- Remedial Actions -->
        <div class="glassy-table border rounded-lg shadow-sm p-4">

          <h6 class="fw-bold text-danger mb-3">Remedial Actions</h6>

          <div v-if="overview?.remedial_actions && overview.remedial_actions.length > 0" class="bg-danger-subtle border-start border-danger border-3 p-3 small">
            <ul class="mb-0">
              <li v-for="(action, idx) in overview.remedial_actions" :key="idx">{{ action }}</li>
            </ul>
          </div>

          <div v-else class="text-muted text-center py-3">
            <p>No remedial actions required</p>
          </div>

        </div>

      </div>

    </div>
  </div>
</main>
</template>


<script>
import axios from 'axios';
import { get_token, GET_MONITORING_OVERVIEW_URL } from '@/utils/constants/constants';

export default {

  name: "MonitoringOverview",

  props: {
    entityProfileId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      hostname: '',
      overview: null,
      loading: false,
      error: null,
    }
  },

  mounted() {
    // Set hostname when component is mounted in browser
    this.hostname = window.location.hostname || '';
    this.fetchOverview();
  },

  computed: {
    inspectorDisplay() {
      const o = this.overview || {};
      // handle several possible backend field shapes
      if (typeof o.inspector === 'string' && o.inspector.trim()) return o.inspector;
      if (o.inspector?.name) return o.inspector.name;
      if (o.inspector?.full_name) return o.inspector.full_name;
      if (o.assigned_inspector_name) return o.assigned_inspector_name;
      if (o.assigned_inspector?.name) return o.assigned_inspector.name;
      return null; // signals "Not Assigned"
    },
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    },
    calculateSectionPercentage(section) {
      if (!section.total_questions || section.total_questions === 0) return 0;
      return Math.round((section.answered_questions / section.total_questions) * 100);
    },
    getMetricClass(value) {
      if (value >= 75) return 'text-success';
      if (value >= 50) return 'text-warning';
      return 'text-danger';
    },
    getMetricBarClass(value) {
      if (value >= 75) return 'bg-success';
      if (value >= 50) return 'bg-warning';
      return 'bg-danger';
    },
    getScoreColor(score) {
      if (score >= 75) return 'bg-success';
      if (score >= 50) return 'bg-warning';
      return 'bg-danger';
    },
    hostnameIncludes(text) {
      // Safe method to check if hostname includes text
      return this.hostname && this.hostname.includes(text);
    },
    async fetchOverview() {
  if (!this.entityProfileId) return;

  this.loading = true;

  try {
    const token = await get_token();

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`
    };

    const params = {
      entity_profile_id: this.entityProfileId
    };

    const { data } = await axios.get(GET_MONITORING_OVERVIEW_URL, { headers, params });

    console.log('FULL API RESPONSE:', data); // 👈 whole response

    if (data?.status === 'success') {
      this.overview = data.data;

      console.log('OVERVIEW DATA:', this.overview); // 👈 main object

      // 🔍 Check assigned inspector fields specifically
      console.log('Inspector (raw):', this.overview.inspector);
      console.log('Assigned Inspector Name:', this.overview.assigned_inspector_name);
      console.log('Assigned Inspector Object:', this.overview.assigned_inspector);

      // 👇 Your computed result
      console.log('Inspector Display:', this.inspectorDisplay);

    } else {
      this.error = data?.message || 'Failed to load overview';
    }

  } catch (e) {
    console.error('ERROR:', e);
    this.error = e.response?.data?.message || e.message;
  } finally {
    this.loading = false;
  }
}
  }

}
</script>
